import { InfoOutlined, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { isUndefined } from 'lodash'
import { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import 'react-phone-input-2/lib/material.css'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../_helpers/hooks'
import { isBusinessURL } from '../../../_helpers/routes'
import { ILoginFormData } from '../../../_models/authenticationInterface'
import LanguageSelector from '../../../components/languageSelector'
import CopyRight from '../../../features/copyRight/CopyRight'
import Logo from '../../../images/logo.svg'
import { authenticationState, signIn } from '../../../slices/authenticationSlice'

const LoginPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [showPassword, setShowPassword] = useState(false)
  const handleClickShowPassword = () => {
    setShowPassword((prevValue) => !prevValue)
  }
  const {
    handleSubmit,
    control,
    setError,
    formState: { isSubmitted },
  } = useForm<ILoginFormData>({
    mode: 'onChange',
  })

  const onSubmit: SubmitHandler<ILoginFormData> = (data) => {
    dispatch(signIn(data))
  }

  const { error } = useAppSelector(authenticationState)
  useEffect(() => {
    if (error.message) {
      setError('email', { type: 'email' })
      setError('password', { type: 'password' })
    }
  }, [error, setError])

  return (
    <Grid item xs={12} sm={6} md={5} component={Paper} elevation={0} square>
      <Grid
        container
        direction='column'
        alignItems='center'
        height='100%'
        justifyContent='center'
        sx={{
          mx: { xs: 'auto' },
          p: { xs: 4, sm: 4, md: 5, lg: 10 },
          maxWidth: '90%',
        }}
      >
        <Grid item sx={{ flexGrow: 1 }}>
          <Grid
            container
            direction='column'
            alignItems='center'
            height='100%'
            justifyContent='center'
          >
            <Grid item>
              <Box
                component='img'
                sx={{
                  height: 33,
                  marginBottom: { xs: 5, md: 8 },
                  cursor: 'pointer',
                }}
                alt={t('loginPage.logoText')}
                src={Logo}
                onClick={() =>
                  isBusinessURL()
                    ? window.location.replace(
                        `${window.location.protocol}//${process.env.REACT_APP_BUSINESS_URL}/`,
                      )
                    : navigate('/')
                }
              />
              <Typography variant='h2'>{t('loginPage.signIn')}</Typography>
              <Box component={'span'} color='secondary.main'>
                <Typography variant='subtitle1' sx={{ my: 2 }}>
                  {t('loginPage.signInText')}
                </Typography>
              </Box>
              <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ mt: 1 }}>
                  <Controller
                    name='email'
                    control={control}
                    defaultValue=''
                    rules={{
                      required: t('loginPage.emailAddressRequired'),
                      maxLength: {
                        value: 64,
                        message: t('loginPage.emailMaxLengthError'),
                      },
                      pattern: {
                        value: /^[a-zA-Z0-9._+]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/,
                        message: t('loginPage.emailValid'),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        margin='normal'
                        fullWidth
                        label={t('loginPage.emailLabel')}
                        error={isSubmitted && !isUndefined(error)}
                        helperText={error?.message}
                        aria-label={t('loginPage.emailLabel')}
                      />
                    )}
                  />
                  <Controller
                    name='password'
                    control={control}
                    defaultValue=''
                    rules={{
                      required: t('loginPage.passwordRequired'),
                      // pattern: {
                      //   value: /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
                      //   message: 'Please enter a valid password',
                      // },
                      // minLength: {
                      //   value: 8,
                      //   message: 'Password must have at least 8 characters',
                      // },
                      // maxLength: {
                      //   value: 16,
                      //   message: 'Password must have less than 16 characters',
                      // },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        margin='normal'
                        fullWidth
                        label={t('loginPage.passwordLabel')}
                        type={!showPassword ? 'password' : 'text'}
                        error={isSubmitted && !isUndefined(error)}
                        helperText={error?.message}
                        aria-label={t('loginPage.passwordLabel')}
                        InputProps={{
                          endAdornment: (
                            <>
                              <InputAdornment position='end'>
                                <IconButton
                                  aria-label='toggle password visibility'
                                  onClick={handleClickShowPassword}
                                  edge='end'
                                  sx={{ color: 'secondary.light', mr: 0 }}
                                >
                                  {showPassword ? (
                                    <VisibilityOutlined />
                                  ) : (
                                    <VisibilityOffOutlined />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {isSubmitted && error?.message && (
                    <Typography variant='body2' my={2} color='error.dark'>
                      <IconButton color='error' sx={{ paddingLeft: '0' }}>
                        <InfoOutlined />
                      </IconButton>
                      {error.message}
                    </Typography>
                  )}

                  <Box>
                    <Link
                      role='link'
                      tabIndex={0}
                      variant='body2'
                      underline='hover'
                      sx={{
                        pt: 1,
                        '&:focus-visible': {
                          outline: `0.0625rem solid ${theme.palette.error.main}`,
                          borderRadius: '0.25rem',
                          padding: '0.3rem',
                        },
                      }}
                      onClick={() => navigate('/forget-password')}
                      fontWeight='400'
                      aria-label={t('loginPage.forgotPassword')}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') navigate('/forget-password')
                      }}
                    >
                      {t('loginPage.forgotPassword')}
                    </Link>
                  </Box>

                  <Button
                    type='submit'
                    fullWidth
                    variant='contained'
                    sx={{ mt: 3, mb: 2 }}
                    size='large'
                    aria-label={t('loginPage.signIn')}
                  >
                    <Typography variant='h5'>{t('loginPage.signIn')}</Typography>
                  </Button>

                  <Typography variant='body2' mt={2} sx={{ textAlign: 'center' }}>
                    {t('loginPage.dontHaveUserAccount')}&nbsp;
                    <Link
                      underline='hover'
                      variant='body2'
                      role='link'
                      tabIndex={0}
                      onClick={() => navigate('/sign-up')}
                      aria-label={t('loginPage.signUp')}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') navigate('/sign-up')
                      }}
                      sx={{
                        '&:focus-visible': {
                          outline: `0.0625rem solid ${theme.palette.error.main}`,
                          borderRadius: '0.25rem',
                          padding: '0.3rem',
                        },
                      }}
                    >
                      {t('loginPage.signUp')}
                    </Link>
                  </Typography>
                </Box>
              </form>
            </Grid>
          </Grid>
        </Grid>

        <Grid mb={2}>
          <LanguageSelector />
        </Grid>
        <Grid item>
          <CopyRight sx={{ mb: 2 }} />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default LoginPage
